/**
 * SubscriptionStatus Component
 * Displays subscription status badge with optional countdown
 */
import { useMemo } from 'react';
import { cn } from '@/utils/cn';
import type { SubscriptionStatus as SubscriptionStatusType } from '@/types/billing';

export interface SubscriptionStatusProps {
  status: SubscriptionStatusType;
  trialEnd?: string | null;
  cancelAt?: string | null;
  className?: string;
}

/**
 * Calculate days remaining until a date
 */
function getDaysRemaining(dateString: string): number {
  const targetDate = new Date(dateString);
  const now = new Date();
  const diffTime = targetDate.getTime() - now.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return Math.max(0, diffDays);
}

/**
 * Format countdown text
 */
function formatCountdown(days: number, type: 'trial' | 'cancel'): string {
  if (days === 0) {
    return type === 'trial' ? 'Trial ends today' : 'Cancels today';
  }
  if (days === 1) {
    return type === 'trial' ? '1 day left in trial' : 'Cancels tomorrow';
  }
  return type === 'trial'
    ? `${days} days left in trial`
    : `Cancels in ${days} days`;
}

/**
 * Status configuration
 */
const statusConfig: Record<
  SubscriptionStatusType,
  { bg: string; text: string; label: string }
> = {
  active: {
    bg: 'bg-green-100',
    text: 'text-green-800',
    label: 'Active',
  },
  trialing: {
    bg: 'bg-blue-100',
    text: 'text-blue-800',
    label: 'Trial',
  },
  past_due: {
    bg: 'bg-yellow-100',
    text: 'text-yellow-800',
    label: 'Past Due',
  },
  canceled: {
    bg: 'bg-red-100',
    text: 'text-red-800',
    label: 'Canceled',
  },
  unpaid: {
    bg: 'bg-red-100',
    text: 'text-red-800',
    label: 'Unpaid',
  },
  incomplete: {
    bg: 'bg-gray-100',
    text: 'text-gray-800',
    label: 'Incomplete',
  },
  incomplete_expired: {
    bg: 'bg-gray-100',
    text: 'text-gray-800',
    label: 'Expired',
  },
  paused: {
    bg: 'bg-gray-100',
    text: 'text-gray-800',
    label: 'Paused',
  },
  expired: {
    bg: 'bg-gray-100',
    text: 'text-gray-800',
    label: 'Expired',
  },
};

export default function SubscriptionStatus({
  status,
  trialEnd,
  cancelAt,
  className,
}: SubscriptionStatusProps) {
  const config = statusConfig[status] || statusConfig.active;

  // Calculate countdown if applicable
  const countdown = useMemo(() => {
    if (status === 'trialing' && trialEnd) {
      const days = getDaysRemaining(trialEnd);
      return formatCountdown(days, 'trial');
    }
    if (cancelAt) {
      const days = getDaysRemaining(cancelAt);
      return formatCountdown(days, 'cancel');
    }
    return null;
  }, [status, trialEnd, cancelAt]);

  return (
    <div className={cn('flex flex-col items-start gap-1', className)}>
      <span
        className={cn(
          'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium',
          config.bg,
          config.text
        )}
      >
        {config.label}
      </span>
      {countdown && (
        <span className="text-xs text-gray-500">{countdown}</span>
      )}
    </div>
  );
}
